// skipcq: JS-W1028
/* eslint-disable react-hooks/set-state-in-effect -- Firestore listener setup resets state when brewery scope changes */
import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  collection,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';

import { BrewerySessionContext } from './BrewerySessionContext';
import useBeerStyles from '../hooks/useBeerStyles';
import { db } from '../utils/firebase';
import { beerMatchesBreweryScope } from '../utils/brewery/breweryBeerFilter';
import { formatBreweryMemberLabel } from '../utils/brewery/formatBreweryMemberLabel';
import { buildStyleMetaMap } from '../utils/brewhousePortfolio/portfolioStyleFilters';

const defaultCatalogContext = {
  displayBeers: [],
  locationFilteredBeers: [],
  loading: true,
  error: null,
  groupMembers: [],
  portfolioBreweryIds: [],
  breweryNameById: {},
  styleMetaMap: {},
  stylesLoading: true,
};

const BrewhouseBeerCatalogContext = React.createContext(defaultCatalogContext);

function mapSnapshotToBeers(snapshot) {
  const rows = snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  }));
  rows.sort((left, right) =>
    (left.name || '').localeCompare(right.name || '', undefined, {
      sensitivity: 'base',
    }),
  );
  return rows;
}

function BrewhouseBeerCatalogProvider({ children }) {
  const {
    breweryId,
    breweryGroupId,
    isLoading: sessionLoading,
    activeLocationLabel,
    activeBreweryName,
    activeBeerScopeBreweryIds = [],
  } = useContext(BrewerySessionContext);
  const { beerStyles, loading: stylesLoading } = useBeerStyles();

  const [localBeers, setLocalBeers] = useState([]);
  const [siblingBeersByBrewery, setSiblingBeersByBrewery] = useState({});
  const [groupMembers, setGroupMembers] = useState([]);
  const [beersLoading, setBeersLoading] = useState(true);
  const [membersLoading, setMembersLoading] = useState(false);
  const [error, setError] = useState(null);

  const normalizedGroupId =
    typeof breweryGroupId === 'string' ? breweryGroupId.trim() : '';
  const hasBreweryGroup = normalizedGroupId.length > 0;

  const groupMemberIds = useMemo(
    () => groupMembers.map((member) => member.id).filter(Boolean),
    [groupMembers],
  );

  const scopeBreweryIds = useMemo(
    () =>
      (Array.isArray(activeBeerScopeBreweryIds)
        ? activeBeerScopeBreweryIds
        : []
      ).filter((scopeId) => typeof scopeId === 'string' && scopeId.trim().length > 0),
    [activeBeerScopeBreweryIds],
  );

  useEffect(() => {
    const normalizedId = typeof breweryId === 'string' ? breweryId.trim() : '';
    if (!normalizedId) {
      setLocalBeers([]);
      setBeersLoading(false);
      return undefined;
    }

    setBeersLoading(true);
    setError(null);

    const collRef = collection(db, 'beer');
    const qry = query(
      collRef,
      where('availableBreweryIds', 'array-contains', normalizedId),
    );

    const unsubscribe = onSnapshot(
      qry,
      (snapshot) => {
        setLocalBeers(mapSnapshotToBeers(snapshot));
        setBeersLoading(false);
      },
      (snapshotError) => {
        console.error('Local brewery beers listener failed:', snapshotError);
        setError('Failed to load beers');
        setLocalBeers([]);
        setBeersLoading(false);
      },
    );

    return () => unsubscribe();
  }, [breweryId]);

  useEffect(() => {
    if (!hasBreweryGroup) {
      setGroupMembers([]);
      setMembersLoading(false);
      return undefined;
    }

    setMembersLoading(true);
    const breweriesRef = collection(db, 'breweries');
    const membersQuery = query(
      breweriesRef,
      where('breweryGroupId', '==', normalizedGroupId),
    );

    const unsubscribe = onSnapshot(
      membersQuery,
      (snapshot) => {
        const rows = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        rows.sort((left, right) =>
          (left.name || left.city || left.id).localeCompare(
            right.name || right.city || right.id,
            undefined,
            { sensitivity: 'base' },
          ),
        );
        setGroupMembers(rows);
        setMembersLoading(false);
      },
      (membersError) => {
        console.error('Group members listener failed:', membersError);
        setGroupMembers([]);
        setMembersLoading(false);
      },
    );

    return () => unsubscribe();
  }, [hasBreweryGroup, normalizedGroupId]);

  useEffect(() => {
    if (!hasBreweryGroup || !breweryId) {
      setSiblingBeersByBrewery({});
      return undefined;
    }

    setSiblingBeersByBrewery({});

    const siblingIds = groupMemberIds.filter((memberId) => memberId !== breweryId);
    if (siblingIds.length === 0) {
      setSiblingBeersByBrewery({});
      return undefined;
    }

    const unsubscribes = siblingIds.map((siblingId) => {
      const collRef = collection(db, 'beer');
      const qry = query(
        collRef,
        where('availableBreweryIds', 'array-contains', siblingId),
      );

      return onSnapshot(
        qry,
        (snapshot) => {
          setSiblingBeersByBrewery((previous) => ({
            ...previous,
            [siblingId]: mapSnapshotToBeers(snapshot),
          }));
        },
        (siblingError) => {
          console.error(`Sibling beers listener failed for ${siblingId}:`, siblingError);
          setSiblingBeersByBrewery((previous) => ({
            ...previous,
            [siblingId]: [],
          }));
        },
      );
    });

    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [hasBreweryGroup, breweryId, groupMemberIds]);

  const displayBeers = useMemo(() => {
    const beersById = new Map();
    localBeers.forEach((beer) => beersById.set(beer.id, beer));
    Object.values(siblingBeersByBrewery).forEach((siblingList) => {
      siblingList.forEach((beer) => {
        if (!beersById.has(beer.id)) {
          beersById.set(beer.id, beer);
        }
      });
    });
    return Array.from(beersById.values()).sort((left, right) =>
      (left.name || '').localeCompare(right.name || '', undefined, {
        sensitivity: 'base',
      }),
    );
  }, [localBeers, siblingBeersByBrewery]);

  const portfolioBreweryIds = useMemo(() => {
    if (scopeBreweryIds.length > 0) {
      return scopeBreweryIds;
    }
    return breweryId ? [breweryId] : [];
  }, [scopeBreweryIds, breweryId]);

  const locationFilteredBeers = useMemo(
    () =>
      displayBeers.filter((beerRow) =>
        beerMatchesBreweryScope(beerRow, portfolioBreweryIds),
      ),
    [displayBeers, portfolioBreweryIds],
  );

  const breweryNameById = useMemo(() => {
    const nameMap = {};
    groupMembers.forEach((member) => {
      if (!member?.id) {
        return;
      }
      nameMap[member.id] = formatBreweryMemberLabel(member);
    });
    if (breweryId && !nameMap[breweryId]) {
      const activeMember = groupMembers.find((member) => member.id === breweryId);
      if (activeMember) {
        nameMap[breweryId] = formatBreweryMemberLabel(activeMember);
      } else {
        const fallbackLabel = activeLocationLabel || activeBreweryName;
        if (fallbackLabel) {
          nameMap[breweryId] = fallbackLabel;
        }
      }
    }
    return nameMap;
  }, [groupMembers, breweryId, activeLocationLabel, activeBreweryName]);

  const styleMetaMap = useMemo(
    () => buildStyleMetaMap(beerStyles),
    [beerStyles],
  );

  const loading = sessionLoading || beersLoading || membersLoading || stylesLoading;

  const value = useMemo(
    () => ({
      displayBeers,
      locationFilteredBeers,
      loading,
      error,
      groupMembers,
      portfolioBreweryIds,
      breweryNameById,
      styleMetaMap,
      stylesLoading,
    }),
    [
      displayBeers,
      locationFilteredBeers,
      loading,
      error,
      groupMembers,
      portfolioBreweryIds,
      breweryNameById,
      styleMetaMap,
      stylesLoading,
    ],
  );

  return (
    <BrewhouseBeerCatalogContext.Provider value={value}>
      {children}
    </BrewhouseBeerCatalogContext.Provider>
  );
}

BrewhouseBeerCatalogProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { BrewhouseBeerCatalogContext, BrewhouseBeerCatalogProvider };
